import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ScreeningVerdict } from '../../data/screening-results.model';
import { ScreeningCreatorRow } from '../../services/screening-results/screening-results.service';
import { VerdictPill } from '../../ui/verdict-pill/verdict-pill';

/** Ring and bar colour per verdict, as CSS custom-property values. */
const RING_COLOUR: Record<ScreeningVerdict, string> = {
  passed: 'var(--color-green-600)',
  flagged: 'var(--color-amber-500)',
  failed: 'var(--color-red-600)',
  progress: 'var(--brand-studio)',
  queued: 'var(--muted-foreground)',
};

/**
 * One creator in the cohort, and how far their screening has got.
 *
 * Every number here comes from that creator's real Noodle results — the card this replaces
 * rendered a hard-coded 10 / 10 / 10 / 10%.
 */
@Component({
  selector: 'scr-creator-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, VerdictPill],
  templateUrl: './creator-card.html',
  styleUrl: './creator-card.scss',
})
export class CreatorCard {

  readonly creator = input.required<ScreeningCreatorRow>();
  readonly briefId = input.required<string>();

  readonly ringColour = computed(() => RING_COLOUR[this.creator().progress.verdict]);

  readonly remaining = computed(() => {
    const progress = this.creator().progress;
    return Math.max(0, progress.total - progress.evaluated);
  });

  readonly footerLabel = computed(() => {
    if (!this.creator().resultsAvailable) {
      return 'Pending evaluation';
    }
    const left = this.remaining();
    return left > 0 ? `${left} item${left === 1 ? '' : 's'} left` : 'All content evaluated';
  });

  /** The report is keyed on our creator uid; the report resolves Noodle's id from the inquiry. */
  readonly reportLink = computed<readonly string[]>(() => [
    '/screening-brief-report',
    this.briefId(),
    this.creator().creatorUserId,
  ]);

  readonly handleLine = computed(() => {
    const creator = this.creator();
    const handle = creator.instagramHandle ?? creator.tiktokHandle;
    const parts: string[] = [];
    if (handle) {
      parts.push(`@${handle}`);
    }
    if (creator.followers) {
      parts.push(`${formatFollowers(creator.followers)} followers`);
    }
    return parts.join(' · ');
  });
}

function formatFollowers(count: number): string {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(count >= 10_000 ? 0 : 1).replace(/\.0$/, '')}k`;
  }
  return String(count);
}
